import {
  calculateAngledAxisBandSize,
  calculateCategoryYAxisWidth,
  calculateVerticalTickAxisBandSize,
  CATEGORY_LABEL_WRAP_WIDTH,
  measureChartTextWidth,
  wrapCategoryLabelLines,
} from '../shared/chartAxisUtils'

export {
  calculateAngledAxisBandSize,
  calculateCategoryYAxisWidth,
  calculateVerticalTickAxisBandSize,
  CATEGORY_LABEL_WRAP_WIDTH,
  measureChartTextWidth,
  wrapCategoryLabelLines,
}

export interface FormatSignedValueOptions {
  decimalPlaces?: number
  showSign?: boolean
  unit?: string
}

/**
 * Computes a symmetric domain around zero from signed values.
 * Returns [-domainMax, domainMax] unless domainOverride is provided.
 */
export function computeSymmetricDomain(
  values: number[],
  domainOverride?: [number | 'auto' | string, number | 'auto' | string]
): { domainMin: number; domainMax: number } {
  if (
    domainOverride &&
    domainOverride[0] !== 'auto' &&
    domainOverride[0] !== undefined &&
    domainOverride[1] !== 'auto' &&
    domainOverride[1] !== undefined
  ) {
    const domainMin = Number(domainOverride[0])
    const domainMax = Number(domainOverride[1])
    const absMax = Math.max(Math.abs(domainMin), Math.abs(domainMax))
    return { domainMin: -absMax, domainMax: absMax }
  }

  const absMax = values.reduce((max, value) => Math.max(max, Math.abs(value)), 0)
  const niceMax = roundToNiceTick(absMax)
  return { domainMin: -niceMax, domainMax: niceMax }
}

function roundToNiceTick(value: number): number {
  if (value === 0) return 1

  const roughTickCount = 5
  const roughInterval = value / roughTickCount
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)))
  const normalizedInterval = roughInterval / magnitude

  let niceInterval: number
  if (normalizedInterval <= 1) {
    niceInterval = magnitude
  } else if (normalizedInterval <= 2) {
    niceInterval = 2 * magnitude
  } else if (normalizedInterval <= 5) {
    niceInterval = 5 * magnitude
  } else {
    niceInterval = 10 * magnitude
  }

  return Math.ceil(value / niceInterval) * niceInterval
}

/**
 * Generates symmetric ticks from -domainMax to domainMax.
 */
export function generateSymmetricTicks(domainMax: number, tickCount = 5): number[] {
  if (domainMax === 0) return [0]

  const span = domainMax * 2
  const roughInterval = span / tickCount
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)))
  const normalizedInterval = roughInterval / magnitude

  let niceInterval: number
  if (normalizedInterval <= 1) {
    niceInterval = magnitude
  } else if (normalizedInterval <= 2) {
    niceInterval = 2 * magnitude
  } else if (normalizedInterval <= 5) {
    niceInterval = 5 * magnitude
  } else {
    niceInterval = 10 * magnitude
  }

  const decimalPlaces = niceInterval >= 1 ? 0 : Math.ceil(-Math.log10(niceInterval))
  const ticks: number[] = []
  for (let tick = -domainMax; tick <= domainMax + niceInterval / 2; tick += niceInterval) {
    ticks.push(parseFloat(tick.toFixed(decimalPlaces)))
  }

  if (!ticks.includes(0)) {
    ticks.push(0)
    ticks.sort((a, b) => a - b)
  }

  return ticks
}

export function formatSignedValue(
  value: number,
  { decimalPlaces = 2, showSign = true, unit }: FormatSignedValueOptions = {}
): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(Math.abs(value))

  const signPrefix = showSign ? (value > 0 ? '+' : value < 0 ? '-' : '') : value < 0 ? '-' : ''
  const signed = `${signPrefix}${formatted}`
  return unit ? `${signed} ${unit}` : signed
}

export function getBarColor(value: number, positiveColor: string, negativeColor: string): string {
  return value >= 0 ? positiveColor : negativeColor
}

const BAR_TIP_RADIUS = 8

/**
 * Radius for the outer tip of each bar. Recharts inverts corner application for
 * negative values, so use the same tip-side radius as ChartBar for both signs.
 */
export function getBarRadius(
  _value: number,
  isHorizontal: boolean
): [number, number, number, number] {
  return isHorizontal
    ? [0, BAR_TIP_RADIUS, BAR_TIP_RADIUS, 0]
    : [BAR_TIP_RADIUS, BAR_TIP_RADIUS, 0, 0]
}
